const AbstractEntity = require('@/lib/Entities/AbstractEntity');
const Category = require('@/lib/Categories/Category');
const GiftAidRate = require('@/models/GiftAidRate');
const PaymentPatterns = require('@/lib/PaymentMethods/PaymentPatterns');
const { toBarcodeArray } = require('@/lib/Barcodes');
const { toBool, toFloat, toInt, toIntArray, toNullableBool, toNullableFloat, toNullableInt, toNullableString, toString } = require('@/functions/transform');

/**
 * Representation of a generic item.
 * This contains the properties shared between all item types
 * (i.e. what's on the items table rather than ticket/products table)
 */
class AbstractItem extends AbstractEntity {
    constructor() {
        super();

        /**
         * @type {?Boolean}
         */
        this.allowPriceEdit = false;

        /**
         * @type {string[]}
         */
        this.barcodes = [];

        /**
         * @type {number[]}
         */
        this.branchIDs = [];

        /**
         * @type {?Category}
         */
        this.category = null;

        /**
         * @type {?number}
         */
        this.catID = null;

        /**
         * @type {?string}
         */
        this.description = null;

        /**
         * @type {boolean}
         */
        this.giftAid = false;

        /**
         * @type {?GiftAidRate}
         */
        this.giftAidRate = null;

        /**
         * @type {?number}
         */
        this.giftAidRateID = null;

        /**
         * A Fontawesome icon name to use to represent this item type.
         * This is only used in the cart and may not be set for every item type if we don't want those to display an
         * icon.
         *
         * @type {?string}
         */
        this.icon = null;

        /**
         * @type {String}
         */
        this.itemType = null;

        /**
         * @type {string}
         */
        this.name = '';

        /**
         * The price of this item when sold online (Customer Facing),
         * and also offline (ProPoint) if offlinePrice is not set.
         * This called "Online Price" in the Back Office. But beware, in the backend code and API this field is just
         * called "price".
         *
         * This value should never be changed. The Back Office/API is the source of truth.
         *
         * @readonly
         * @type {number}
         */
        this.onlinePrice = undefined;

        /**
         * The price of this item when sold offline (ProPoint).
         * This may be null (or 0), in which case the price is "onlinePrice".
         *
         * This value should never be changed. The Back Office/API is the source of truth.
         *
         * @readonly
         * @type {?Number}
         */
        this.offlinePrice = null;

        /**
         * This is the price of the item before any adjustments such as pricing rules are applied.
         * It is simply the determination of which of onlinePrice/offlinePrice to use.
         *
         * This value should never be changed. The Back Office/API is the source of truth.
         *
         * This is set to either offlinePrice or onlinePrice if not already set upon hydration.
         *
         * @readonly
         * @type {?Number}
         */
        this.initialPrice = null;

        /**
         * Default payment pattern set to single but this will usually come from the API.
         *
         * @type {string}
         */
        this.paymentPattern = PaymentPatterns.SINGLE;

        /**
         * The current price of the item.
         * Most of the time this will be the same as initialPrice. However it may change for tickets with pricing rules.
         * This is set to the same as initialPrice if not already set upon hydration.
         *
         * @type {?Number}
         */
        this.price = null;

        /**
         * @type {?String}
         */
        this.shortName = null;
    }

    /**
     * @return {string}
     */
    getName() {
        return this.shortName || this.name;
    }

    /**
     * @returns {number}
     */
    getPrice() {
        return this.price;
    }

    /**
     * @returns {boolean}
     */
    hasAdjustedPrice() {
        return this.price !== this.initialPrice;
    }

    /**
     * @return {object}
     */
    getHydrationMap() {
        return Object.assign(
            super.getHydrationMap(),
            {
                allowPriceEdit: toNullableBool,
                barcodes: toBarcodeArray,
                branchIDs: toIntArray,
                category: {
                    field: ['category', 'categories'],
                    model: Category,
                },
                catID: toInt,
                description: toNullableString,
                giftAid: toBool,
                giftAidRate: {
                    model: GiftAidRate,
                },
                giftAidRateID: {
                    field: ['giftAidRateID', 'giftaidRateID'],
                    transform: toNullableInt,
                },
                ID: {
                    field: ['ID', 'itemID'],
                    transform: toInt,
                },
                // initialPrice is calculated in afterHydration
                name: toString,
                offlinePrice: toNullableFloat,
                onlinePrice: toNullableFloat,
                paymentPattern: toString,
                // price is calculated in afterHydration
                shortName: toNullableString,
            }
        );
    }

    afterHydration(data = {}) {
        // When items come from the API they have the following fields:
        // - price: The online price of the item
        // - offlinePrice: The price of the item when sold offline (i.e. on ProPoint). This may be 0, in which case
        //   the price (online price) should be used.
        //
        // But once items are in ProPoint the price field has a different meaning:
        // - onlinePrice: The online price of the item
        // - offlinePrice: The price of the item when sold offline (i.e. on ProPoint). This may be 0, in which case
        //   the price (online price) should be used.
        // - price: The calculated price of the item (online or offline) which may also be adjusted due to
        //   discounts, GiftAid, etc.
        //
        // We need to handle hydrating items for the first time with data from the API, and also rehydrating items
        // that have been saved in IndexedDB already.

        // When data comes from the API for the first time we need to use the price field as onlinePrice instead
        // and not let it go directly to the price field.
        if (!data.isHydrated && !data.hasOwnProperty('onlinePrice') && this.onlinePrice === undefined) {
            this.onlinePrice = toFloat(data.price || 0.00);
            delete data.price;
        }

        // offlinePrice and onlinePrice should be immutable so we can always use them to recalculate initialPrice.
        // Note that this ternary will prevent being able to set the offline price to 0.
        // In the Back Office when you leave the offlinePrice blank it defaults to 0 (not null)
        // so it must be done this way unless that is changed.
        this.initialPrice = toFloat(this.offlinePrice ? this.offlinePrice : this.onlinePrice);

        // Now that we have removed any confusing price fields, we can set the price property if a value remains.
        if (data.hasOwnProperty('price')) {
            this.price = toFloat(data.price);
        }

        // Set the price if it is not set yet to initialPrice.
        if (this.price === null) {
            this.price = this.initialPrice;
        }

        // If allowPriceEdit is null inherit whatever is set on the category.
        if (this.allowPriceEdit === null) {
            this.allowPriceEdit = this.category ? !!this.category.allowPriceEdit : false;
        }
    }

    /**
     * @return {boolean}
     */
    isSessionBased() {
        return false;
    }

    isActive() {
        return this.status === 'Active';
    }

    isForBranch(branchID) {
        return this.branchIDs.includes(branchID);
    }
}

module.exports = AbstractItem;
